import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  AppBar, Toolbar, IconButton, Typography, Drawer, Box, MenuItem,
  useMediaQuery, useTheme, Tooltip
} from "@mui/material";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import CloseIcon from "@mui/icons-material/Close";
import LogoutIcon from "@mui/icons-material/Logout";
import { LearnersProfile } from "../../../../helper/base_url";
import { isTokenExpired } from "../../../../utils/tokenUtils";
import { jwtDecode } from "jwt-decode";

const LearnersHeader = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [user, setUser] = useState({ name: "" });
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();

  const logout = () => {
    localStorage.removeItem("token");
    navigate("/learners/signin");
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token || isTokenExpired(token)) {
      logout();
      return;
    }

    try {
      const decoded = jwtDecode(token);
      const expiryTime = decoded.exp * 1000;
      const currentTime = Date.now();
      const timeout = expiryTime - currentTime;

      const timer = setTimeout(() => logout(), timeout);

      axios
        .get(LearnersProfile, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => setUser({ name: res.data?.data?.name || "" }))
        .catch((err) => {
          console.error("Profile fetch error:", err);
          logout();
        });

      return () => clearTimeout(timer);
    } catch (error) {
      console.error("Token decode failed:", error);
      logout();
    }
  }, []);

  // No Dashboard or Menu items
  const renderMenuLinks = () => null;

  const firstLetter = user.name?.[0]?.toUpperCase() || "";

  return (
    <AppBar position="static" sx={{ bgcolor: "#fff", boxShadow: "none" }}>
      <Toolbar
        sx={{
          justifyContent: "space-between",
          px: { xs: 2, sm: 6 },
          boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
        }}
      >
        <Typography
          component={Link}
          to="#"
          sx={{
            fontSize: "40px",
            fontWeight: 700,
            color: "rgb(2, 41, 100)",
            textDecoration: "none",
          }}
        >
          Hue<span style={{ color: "rgb(27, 110, 244)" }}>bits</span>
        </Typography>

        {isSmall ? (
          <IconButton onClick={() => setMenuOpen(true)} sx={{ color: "#022964" }}>
            <MenuOutlinedIcon fontSize="large" />
          </IconButton>
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
            {renderMenuLinks()}

            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <IconButton
                sx={{
                  width: 42,
                  height: 42,
                  bgcolor: "#1c84e4",
                  color: "#fff",
                  fontWeight: "bold",
                  fontSize: "18px",
                  "&:hover": { bgcolor: "#1565c0" },
                }}
              >
                {firstLetter}
              </IconButton>

              <Tooltip title="Logout">
                <IconButton onClick={logout} sx={{ color: "#052659" }}>
                  <LogoutIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        )}
      </Toolbar>

      <Drawer
        anchor="left"
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        sx={{ "& .MuiDrawer-paper": { bgcolor: "#fff", width: 250 } }}
      >
        <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1 }}>
          <IconButton onClick={() => setMenuOpen(false)} sx={{ color: "#052659" }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ p: 2, textAlign: "center" }}>
          <IconButton
            sx={{
              width: 60,
              height: 60,
              bgcolor: "#1c84e4",
              color: "#fff",
              fontWeight: "bold",
              fontSize: "22px",
              mb: 3,
              "&:hover": { bgcolor: "#1565c0", transform: "scale(1.1)" },
              "&:active": { transform: "scale(0.95)", bgcolor: "#0d47a1" },
            }}
          >
            {firstLetter}
          </IconButton>

          {renderMenuLinks()}

          <MenuItem
            onClick={() => {
              setMenuOpen(false);
              logout();
            }}
            sx={{
              color: "#d32f2f",
              fontWeight: 500,
              fontSize: "16px",
              borderRadius: "8px",
              p: "12px",
              "&:hover": { bgcolor: "#ffe5e5" },
            }}
          >
            Logout
          </MenuItem>
        </Box>
      </Drawer>
    </AppBar>
  );
};

export default LearnersHeader;
